import SEO from '../components/SEO';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resendVerification } from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

// ── Shared side panel ─────────────────────────────────────────────────────────
function AuthSide({ img, heading, sub, footer }) {
  return (
    <div className="hidden lg:flex lg:w-1/2 relative bg-purple-800">
      <img src={img} alt="MDS Transportation" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
        {/* Logo text — visible on dark bg */}
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center font-bold text-white text-sm">MDS</div>
          <span className="font-bold text-lg">MDS Transportation LLC</span>
        </Link>
        <div>
          <h2 className="text-4xl font-bold mb-4 font-heading">{heading}</h2>
          <p className="text-purple-200">{sub}</p>
        </div>
        <p className="text-purple-400 text-sm">{footer}</p>
      </div>
    </div>
  );
}

// ── Back-to-site button ────────────────────────────────────────────────────────
function BackButton() {
  return (
    <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-purple-700 transition-colors mb-6">
      <ArrowLeft size={15} /> Back to Website
    </Link>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────
export function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedEmail(null);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.first_name}!`);
      navigate(user.role === 'user' ? '/dashboard' : '/admin');
    } catch (err) {
      const data = err.response?.data;
      if (data?.email_not_verified) {
        setUnverifiedEmail(data.email || form.email);
      } else {
        toast.error(data?.message || 'Invalid email or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setResendLoading(true);
    try {
      await resendVerification(unverifiedEmail);
      setResendSent(true);
      toast.success('Verification email resent! Check your inbox.');
    } catch {
      toast.error('Could not resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <SEO title="Sign In" path="/login" noIndex={true} />
      <AuthSide
        img="/images/fleet-parking-lot.jpg"
        heading={<>Safe Journeys,<br />Compassionate Care</>}
        sub="Access your dashboard to manage rides, track bookings, and receive ride notifications."
        footer={`© ${new Date().getFullYear()} MDS Transportation LLC`}
      />
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <BackButton />
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 font-heading">Welcome Back</h1>
            <p className="text-gray-500 mt-2">Sign in to your MDS Transportation account</p>
          </div>
          <form onSubmit={handleSubmit} className="card shadow-sm border border-gray-100 space-y-5">

            {/* Email not verified banner */}
            {unverifiedEmail && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-amber-800 font-semibold text-sm mb-1">📧 Email Not Verified</p>
                <p className="text-amber-700 text-xs mb-3">
                  Please verify your email address before signing in. Check your inbox for the verification link.
                </p>
                {!resendSent ? (
                  <button type="button" onClick={handleResend} disabled={resendLoading}
                    className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:opacity-60 flex items-center gap-1">
                    {resendLoading ? '…Sending' : '🔁 Resend Verification Email'}
                  </button>
                ) : (
                  <p className="text-xs text-green-700 font-semibold">✅ Verification email sent! Check your inbox.</p>
                )}
              </div>
            )}
            <div>
              <label className="label">Email Address</label>
              <input type="email" className="input-field" required placeholder="you@example.com"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label mb-0">Password</label>
                <Link to="/forgot-password" className="text-xs text-purple-600 hover:underline">Forgot password?</Link>
              </div>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} className="input-field pr-10" required placeholder="••••••••"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
              {loading && <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
            <p className="text-center text-sm text-gray-500">
              Don't have an account? <Link to="/register" className="text-purple-700 font-semibold hover:underline">Create one</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Register ──────────────────────────────────────────────────────────────────
export function RegisterPage() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) { toast.error('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await register(form); // returns { success, message, email_verification_required }
      setRegisteredEmail(form.email);
      setRegistered(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── Success: awaiting verification ────────────────────────────────────────
  if (registered) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-10 text-center">
          <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email!</h1>
          <p className="text-gray-600 mb-2">
            We've sent a verification link to:
          </p>
          <p className="text-purple-700 font-semibold text-lg mb-6">{registeredEmail}</p>
          <div className="bg-purple-50 rounded-xl p-4 text-left mb-6">
            <p className="text-sm text-gray-700 font-semibold mb-2">Next steps:</p>
            <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
              <li>Open the email from MDS Transportation</li>
              <li>Click the <strong>"Verify My Email"</strong> button</li>
              <li>Return here to sign in</li>
            </ol>
          </div>
          <p className="text-xs text-gray-400 mb-6">
            Didn't receive it? Check your spam folder, or{' '}
            <Link to="/login" className="text-purple-600 hover:underline">go to sign in to resend</Link>.
          </p>
          <Link to="/login" className="inline-block bg-purple-700 text-white px-8 py-3 rounded-xl font-semibold hover:bg-purple-800 transition-colors">
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <SEO title="Create Account" path="/register" noIndex={true} />
      <AuthSide
        img="/images/fleet-silver-branded.jpg"
        heading={<>Your Health,<br />Our Priority</>}
        sub="Create an account to easily book rides, track your transportation, and receive reminders."
        footer="Serving Fort Wayne, Indianapolis and nearby counties"
      />
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50 overflow-y-auto">
        <div className="w-full max-w-md">
          <BackButton />
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 font-heading">Create Account</h1>
            <p className="text-gray-500 mt-2">Join MDS Transportation for easy ride booking</p>
          </div>
          <form onSubmit={handleSubmit} className="card shadow-sm border border-gray-100 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input className="input-field" required placeholder="John"
                  value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input className="input-field" required placeholder="Doe"
                  value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="label">Email Address</label>
              <input type="email" className="input-field" required placeholder="you@example.com"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="label">Phone Number</label>
              <input className="input-field" required placeholder="+1 (555) 000-0000"
                value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label className="label">Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} className="input-field pr-10" required minLength={8} placeholder="Min. 8 characters"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">Confirm Password</label>
              <input type="password" className="input-field" required placeholder="Repeat your password"
                value={form.confirm_password} onChange={e => setForm({ ...form, confirm_password: e.target.value })} />
            </div>
            <p className="text-xs text-gray-500">
              By creating an account, you agree to our{' '}
              <Link to="/terms" target="_blank" className="text-purple-600 hover:underline">Terms of Service</Link>{' '}
              and{' '}
              <Link to="/privacy" target="_blank" className="text-purple-600 hover:underline">Privacy Policy</Link>.
            </p>
            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
              {loading && <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
            <p className="text-center text-sm text-gray-500">
              Already have an account? <Link to="/login" className="text-purple-700 font-semibold hover:underline">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
